/**
 * Los contadores por estado de stock, que a la vez filtran la tabla.
 *
 * Antes eran el encabezado de una tabla de Alertas aparte, que listaba los
 * mismos productos que la tabla grande de abajo con casi las mismas columnas.
 * Ahora son la unica señal: "2 negativos" en rojo dice cuanto hay que atender
 * sin abrir nada, y tocarlo deja en la tabla solo esos.
 *
 * EL ESTADO QUE HAY QUE EXPLICAR ES EL NEGATIVO
 * ---------------------------------------------
 * Un stock negativo parece un error de sistema y no lo es. Significa que se
 * vendieron mas unidades de las que figuraban cargadas, que en un local pasa
 * todas las semanas: llega mercaderia, se vende de la caja antes de que alguien
 * la ingrese, y la venta descuenta igual. No se arregla escribiendo el numero
 * correcto sino cargando el ingreso que falto. Por eso, con un chip activo, la
 * barra de filtros muestra la ayuda de ese estado (`AYUDA_ESTADO`), justo
 * debajo del chip que lo prendio.
 */
import { Segmented, type OpcionSegmented } from "@/components/ui/segmented";
import type { EstadoStock } from "@/lib/tipos";

import { ESTADOS_ALERTA, ETIQUETA_ESTADO, VARIANTE_ESTADO } from "./logica";

export interface ChipsEstadoProps {
  /** Cuantos productos hay en cada estado. Sale de las alertas, sin filtrar. */
  cuenta: Record<EstadoStock, number>;
  /** El chip activo; "" = todos. */
  estado: "" | EstadoStock;
  onEstado: (e: "" | EstadoStock) => void;
  className?: string;
}

type Valor = "" | EstadoStock;

export function ChipsEstado({
  cuenta,
  estado,
  onEstado,
  className,
}: ChipsEstadoProps): JSX.Element {
  // "Todos" es solo el boton de sacar el filtro: no lleva numero. Los demas
  // llevan el contador con el tono de su gravedad (rojo para lo que falta,
  // ambar para lo que esta por faltar), y el nombre accesible lo dice en el
  // orden en que se lee en voz alta: "2 Negativo".
  const opciones: ReadonlyArray<OpcionSegmented<Valor>> = [
    { valor: "", etiqueta: "Todos" },
    ...ESTADOS_ALERTA.map((e): OpcionSegmented<Valor> => {
      const variante = VARIANTE_ESTADO[e];
      return {
        valor: e,
        etiqueta: ETIQUETA_ESTADO[e],
        contador: cuenta[e],
        tono: variante === "success" ? undefined : variante,
        "aria-label": `${cuenta[e]} ${ETIQUETA_ESTADO[e]}`,
      };
    }),
  ];

  return (
    <Segmented
      apariencia="chips"
      etiqueta="Filtrar por estado"
      opciones={opciones}
      valor={estado}
      // Tocar el chip activo lo apaga: el filtro es prendible/apagable.
      onChange={(v) => onEstado(v !== "" && v === estado ? "" : v)}
      className={className}
    />
  );
}
